import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import readableFileSize from 'filesize';
import { Url } from '@wix/da-url';
import { BiData, BuyCoreButtonBiEvent } from '@wix/da-bi/pkg/events';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { CoreLevel } from '@wix/da-react-context/pkg/publicSession';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';
import InfoIcon from '@wix/da-ds/pkg/Icons/24x24/Info';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { STORAGE_CAPACITY_LOW_THRESHOLD } from '../constants';

import s from './StorageCapacity.scss';

export interface Props {
  coreLevel: CoreLevel;
  maxCapacity: number;
  freeCapacity: number;
  className?: string;
}

const StorageCapacity: FC<Props> = ({ className, coreLevel, maxCapacity, freeCapacity }) => {
  const { t } = useTranslation();
  const hasNoCapacity = freeCapacity <= 0;
  const hasLowCapacity = freeCapacity <= STORAGE_CAPACITY_LOW_THRESHOLD;
  const usedCapacity = maxCapacity - freeCapacity;
  const canUpgradeCapacity = !coreLevel || coreLevel === 'core_access';

  return (
    <div className={cn(s['root'], className)}>
      <span className={cn(s['capacity'], hasLowCapacity && s['low'])}>
        {t('studio.header.storageCapacity.text', {
          used: readableFileSize(Math.max(0, usedCapacity)),
          max: readableFileSize(maxCapacity),
        })}
        {hasLowCapacity ? (
          <SimpleTooltip
            containerClassName={s['tooltip-container']}
            text={
              <>
                <div>
                  {hasNoCapacity && t('studio.header.storageCapacity.tooltip.noCapacity')}
                  {!hasNoCapacity &&
                    hasLowCapacity &&
                    t('studio.header.storageCapacity.tooltip.lowCapacity')}
                </div>
                {canUpgradeCapacity &&
                  t('studio.header.storageCapacity.tooltip.upgrade', {
                    postProcess: 'reactPostprocessor',
                    upgradeLink: (
                      <PlainButtonOrLink
                        className={s['tooltip-upgrade-link']}
                        href={Url.coreMembershipLink()}
                        target="_blank"
                        biData={BiData<BuyCoreButtonBiEvent>({
                          evid: 375,
                          referral_info: ReferralType.STUDIO_SIZE_LIMIT_TOOLTIP,
                        })}
                      >
                        {t('studio.header.storageCapacity.tooltip.upgradeLink')}
                      </PlainButtonOrLink>
                    ),
                  })}
              </>
            }
            usePortal
          >
            <InfoIcon size={IconSize.SMALL} disableDefaultColors />
          </SimpleTooltip>
        ) : null}
      </span>
    </div>
  );
};
StorageCapacity.displayName = 'StorageCapacity';

export default StorageCapacity;
